// Argus — FloatingWindow: wrapper Rnd + Paper MUI cu titlebar custom (icon, title, close).
// Drag handle = titlebar. Resize handle = corners + edges (default react-rnd).

import { type ReactNode, useCallback, useState, useEffect } from 'react';
import { Rnd, type RndDragCallback, type RndResizeCallback } from 'react-rnd';
import { Box, IconButton, Paper, Tooltip, Typography, useTheme } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import MinimizeIcon from '@mui/icons-material/Minimize';
import CropSquareIcon from '@mui/icons-material/CropSquare';
import FilterNoneIcon from '@mui/icons-material/FilterNone';
import { closePanel, bringToFront, updateRect, toggleMaximize, minimizeWindow } from '../../lib/windowManager';
import { getTaskbarButtonRect } from '../../lib/taskbarButtonRefs';

const TITLEBAR_CLASS = 'argus-floating-titlebar';
const TITLEBAR_HEIGHT = 36;
const MIN_WIDTH = 280;
const MIN_HEIGHT = 200;

export interface FloatingWindowProps {
  windowKey: string;
  title: string;
  icon?: ReactNode;
  iconColor?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  zOrder: number;
  isMaximized?: boolean;
  isMinimized?: boolean;
  children?: ReactNode;
}

export function FloatingWindow({
  windowKey,
  title,
  icon,
  iconColor,
  x,
  y,
  width,
  height,
  zOrder,
  isMaximized = false,
  isMinimized = false,
  children,
}: FloatingWindowProps): JSX.Element {
  const theme = useTheme();
  const [maxRect, setMaxRect] = useState<{ x: number; y: number; width: number; height: number } | null>(null);
  const [minimizeTransform, setMinimizeTransform] = useState<string | null>(null);

  // Compute fill-canvas rect when maximized; recompute on resize.
  useEffect(() => {
    if (!isMaximized) { setMaxRect(null); return; }
    const compute = () => {
      const canvas = document.querySelector('.argus-dashboard-canvas') as HTMLElement | null;
      if (!canvas) return;
      setMaxRect({ x: 0, y: 0, width: canvas.clientWidth, height: canvas.clientHeight });
    };
    compute();
    window.addEventListener('resize', compute);
    return () => window.removeEventListener('resize', compute);
  }, [isMaximized]);

  const effectiveRect = isMaximized && maxRect ? maxRect : { x, y, width, height };

  // Win11 genie effect: minimize → translate la taskbar button center + scale 0.05 + opacity 0.
  // Restore → revine la transform 'none'. Tranzitia CSS aplicată pe Paper.
  useEffect(() => {
    if (!isMinimized) { setMinimizeTransform(null); return; }
    // Defer un frame ca să fie taskbar buttons rendered + getBoundingClientRect valid.
    const raf = window.requestAnimationFrame(() => {
      const buttonRect = getTaskbarButtonRect(windowKey);
      const canvas = document.querySelector('.argus-dashboard-canvas') as HTMLElement | null;
      const canvasOffsetTop = canvas?.getBoundingClientRect().top ?? 56;
      const canvasOffsetLeft = canvas?.getBoundingClientRect().left ?? 0;
      const winCenterViewportX = canvasOffsetLeft + effectiveRect.x + effectiveRect.width / 2;
      const winCenterViewportY = canvasOffsetTop + effectiveRect.y + effectiveRect.height / 2;
      if (buttonRect) {
        const btnCenterX = buttonRect.left + buttonRect.width / 2;
        const btnCenterY = buttonRect.top + buttonRect.height / 2;
        const dx = btnCenterX - winCenterViewportX;
        const dy = btnCenterY - winCenterViewportY;
        setMinimizeTransform(`translate(${dx}px, ${dy}px) scale(0.05)`);
      } else {
        setMinimizeTransform('scale(0.05)');
      }
    });
    return () => window.cancelAnimationFrame(raf);
  }, [isMinimized, windowKey, effectiveRect.x, effectiveRect.y, effectiveRect.width, effectiveRect.height]);

  const handleDragStop: RndDragCallback = useCallback(
    (_event, data) => {
      updateRect(windowKey, { x: data.x, y: data.y, width, height });
    },
    [windowKey, width, height]
  );

  const handleResizeStop: RndResizeCallback = useCallback(
    (_event, _direction, ref, _delta, position) => {
      updateRect(windowKey, {
        x: position.x,
        y: position.y,
        width: ref.offsetWidth,
        height: ref.offsetHeight,
      });
    },
    [windowKey]
  );

  return (
    <Rnd
      size={{ width: effectiveRect.width, height: effectiveRect.height }}
      position={{ x: effectiveRect.x, y: effectiveRect.y }}
      minWidth={MIN_WIDTH}
      minHeight={MIN_HEIGHT}
      bounds=".argus-dashboard-canvas"
      dragHandleClassName={TITLEBAR_CLASS}
      onDragStop={handleDragStop}
      onResizeStop={handleResizeStop}
      onMouseDown={() => { if (!isMinimized) bringToFront(windowKey); }}
      disableDragging={isMaximized || isMinimized}
      enableResizing={!isMaximized && !isMinimized}
      style={{
        zIndex: 100 + zOrder,
        // Rnd resize handles sunt OUTSIDE Paper. `pointerEvents:none` pe wrapper previne cursor
        // resize V/H pe edges la ferestre minimized. NU folosim `visibility:hidden` — animația
        // genie-effect minimize/restore (220ms transform+opacity pe Paper) trebuie să rămână
        // vizibilă în tranziție.
        pointerEvents: isMinimized ? 'none' : 'auto',
      }}
    >
      <Paper
        elevation={6}
        sx={{
          width: '100%',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          border: 1,
          borderColor: 'divider',
          backgroundColor: 'background.paper',
          // Win11 genie effect transition pentru minimize/restore.
          transformOrigin: '50% 50%',
          transform: minimizeTransform ?? 'none',
          opacity: isMinimized ? 0 : 1,
          pointerEvents: isMinimized ? 'none' : 'auto',
          transition: 'transform 220ms cubic-bezier(0.55, 0.085, 0.68, 0.53), opacity 200ms ease-out',
        }}
      >
        <Box
          className={TITLEBAR_CLASS}
          sx={{
            height: TITLEBAR_HEIGHT,
            display: 'flex',
            alignItems: 'center',
            px: 1.25,
            backgroundColor: theme.palette.mode === 'dark' ? '#0F1419' : '#E5E7EB',
            borderBottom: 1,
            borderColor: 'divider',
            cursor: 'move',
            userSelect: 'none',
            gap: 1,
          }}
        >
          {icon ? (
            <Box sx={{ color: iconColor ?? 'text.secondary', display: 'flex', alignItems: 'center' }}>
              {icon}
            </Box>
          ) : null}
          <Typography
            variant="body2"
            sx={{ flexGrow: 1, fontWeight: 600, fontSize: 13, color: 'text.primary' }}
            noWrap
          >
            {title}
          </Typography>
          <Tooltip title="Minimize">
            <IconButton
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); bringToFront(windowKey); minimizeWindow(windowKey); }}
              sx={{ p: 0.5 }}
              aria-label="Minimize window"
            >
              <MinimizeIcon sx={{ fontSize: 16 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title={isMaximized ? 'Restore' : 'Maximize'}>
            <IconButton
              size="small"
              onMouseDown={(e) => e.stopPropagation()}
              onClick={(e) => { e.stopPropagation(); bringToFront(windowKey); toggleMaximize(windowKey); }}
              sx={{ p: 0.5 }}
              aria-label={isMaximized ? 'Restore window' : 'Maximize window'}
            >
              {isMaximized ? <FilterNoneIcon sx={{ fontSize: 14 }} /> : <CropSquareIcon sx={{ fontSize: 16 }} />}
            </IconButton>
          </Tooltip>
          <IconButton
            size="small"
            onMouseDown={(e) => e.stopPropagation()}
            onClick={(e) => {
              e.stopPropagation();
              bringToFront(windowKey);
              closePanel(windowKey);
            }}
            sx={{ p: 0.5, '&:hover': { backgroundColor: 'error.dark', color: 'common.white' } }}
            aria-label="Close window"
          >
            <CloseIcon sx={{ fontSize: 16 }} />
          </IconButton>
        </Box>

        <Box sx={{ flexGrow: 1, overflow: 'auto', p: 1.5 }}>{children}</Box>
      </Paper>
    </Rnd>
  );
}
